import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, functions } from '../firebase-config';

export const PENDING_OWNER_STAFF_INVITE_TOKEN_KEY = 'pendingOwnerStaffInviteToken';
const STORAGE_KEY = PENDING_OWNER_STAFF_INVITE_TOKEN_KEY;

/**
 * Public route: `/owner/staff-invite?token=…` — accepts team access after sign-in.
 */
function OwnerStaffInvitePage() {
  const [searchParams] = useSearchParams();
  const [message, setMessage] = useState('Checking your session…');
  const [authUser, setAuthUser] = useState(auth.currentUser);
  const attemptKeyRef = useRef('');

  const token = (searchParams.get('token') || '').trim();
  const inviteEmail = (searchParams.get('email') || '').trim();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, setAuthUser);
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!token) {
      setMessage('This team invite link is missing a token.');
      return;
    }
    try {
      sessionStorage.setItem(STORAGE_KEY, token);
    } catch {
      /* ignore */
    }

    if (!authUser?.uid) {
      setMessage(
        'Sign in (or create an owner account) using the same email this invite was sent to, then we will finish connecting your access.'
      );
      return;
    }

    const key = `${authUser.uid}:${token}`;
    if (attemptKeyRef.current === key) return;
    attemptKeyRef.current = key;

    (async () => {
      setMessage('Connecting your account…');
      try {
        const fn = httpsCallable(functions, 'acceptOwnerStaffInvite');
        const { data } = await fn({ token });
        if (!data?.success) {
          setMessage('Could not accept the invitation.');
          return;
        }
        try {
          sessionStorage.removeItem(STORAGE_KEY);
        } catch {
          /* ignore */
        }
        setMessage('You are connected. Redirecting…');
        window.location.replace('/owner');
      } catch (e) {
        attemptKeyRef.current = '';
        setMessage(e?.message || String(e));
      }
    })();
  }, [token, authUser]);

  const authHref = `/?staffInviteToken=${encodeURIComponent(token || '')}${inviteEmail ? `&email=${encodeURIComponent(inviteEmail)}` : ''}#auth`;

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 45%, #ddd6fe 100%)',
        padding: '20px',
        fontFamily: 'Inter, system-ui, sans-serif',
      }}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          padding: '40px',
          maxWidth: '480px',
          width: '100%',
          textAlign: 'center',
          boxShadow: '0 10px 40px rgba(15, 23, 42, 0.12)',
          border: '1px solid #e2e8f0',
        }}
      >
        <h1 style={{ margin: '0 0 12px', fontSize: '22px', color: '#0f172a' }}>Team invitation</h1>
        <p style={{ margin: 0, color: '#475569', lineHeight: 1.55, fontSize: '15px' }}>{message}</p>
        {!authUser?.uid ? (
          <div style={{ marginTop: '24px' }}>
            <a href={authHref} style={{ display: 'inline-block', textDecoration: 'none' }}>
              <span
                style={{
                  display: 'inline-block',
                  background: '#6366f1',
                  color: '#fff',
                  fontWeight: 700,
                  padding: '12px 22px',
                  borderRadius: '10px',
                  fontSize: '15px',
                }}
              >
                Sign in or sign up
              </span>
            </a>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default OwnerStaffInvitePage;
